<template>
  <v-container fluid class="dash mt-0 pt-2">
    <!-- the week -->
    <section class="dash-hero">
      <div class="dash-dateline">
        <v-btn
          icon
          small
          @click="goToWeek(-1)"
          aria-label="Previous week"
          class="dash-noprint"
          ><v-icon>chevron_left</v-icon></v-btn
        >
        <v-dialog v-model="datePickerDialog" persistent width="290px">
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="dash-date"
              v-bind="attrs"
              v-on="on"
              aria-label="Pick a week"
            >
              {{ weekLabel }}
            </button>
          </template>
          <v-date-picker
            v-model="pickedDate"
            :max="todayKey"
            :first-day-of-week="1"
            scrollable
            color="primary"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="datePickerDialog = false">
              Cancel
            </v-btn>
            <v-btn text color="primary" @click="goToDate(pickedDate)">
              Go to week
            </v-btn>
          </v-date-picker>
        </v-dialog>
        <v-btn
          icon
          small
          @click="goToWeek(1)"
          :disabled="isThisWeek"
          aria-label="Next week"
          class="dash-noprint"
          ><v-icon>chevron_right</v-icon></v-btn
        >
        <v-btn
          v-if="!isThisWeek"
          text
          small
          color="primary"
          class="ml-1 dash-noprint"
          @click="goToDate(todayKey)"
          >Back to this week</v-btn
        >
        <v-spacer></v-spacer>
        <div class="dash-noprint">
          <v-btn
            text
            small
            class="dash-quiet-btn"
            :loading="loadingData"
            @click="getWeek()"
            ><v-icon left small>refresh</v-icon>Refresh</v-btn
          >
          <v-btn
            text
            small
            class="dash-quiet-btn"
            :disabled="!jobs.length"
            @click="downloadCSV"
            ><v-icon left small>download</v-icon>CSV</v-btn
          >
        </div>
      </div>

      <v-skeleton-loader
        v-if="loading"
        type="heading"
        class="mt-3"
        max-width="520"
      ></v-skeleton-loader>
      <template v-else>
        <h1 class="dash-headline">{{ headline }}</h1>
        <p class="dash-subline" v-if="jobs.length">
          {{ garbageLine }}
          <span v-if="totals.pendingTickets"
            >{{ plural(totals.pendingTickets, "load") }}
            {{ totals.pendingTickets === 1 ? "is" : "are" }} still waiting on a
            dump ticket, so tons will go up.</span
          >
        </p>
        <p class="dash-subline dash-subline--note">
          Monday to Friday; weekend work isn't counted.
          <span v-if="inProgress"
            >Through {{ throughLabel }}, with today's punch-ins counted
            live.</span
          >
          A job with more than one OM is listed under each of them and counted
          once in the totals.
        </p>
      </template>
    </section>

    <v-alert v-if="error" type="error" outlined dense class="mb-6">
      {{ error }}
    </v-alert>

    <v-card v-if="loading" outlined class="dash-panel">
      <v-skeleton-loader type="table-tbody" class="px-4"></v-skeleton-loader>
    </v-card>
    <p v-else-if="!jobs.length && !error" class="dash-empty">
      No job had a punch-in or a load hauled this week.
    </p>

    <!-- one panel per OM -->
    <v-card
      v-for="group in groups"
      :key="group.key"
      outlined
      class="dash-panel dash-print"
    >
      <div class="dash-panel__head">
        <div>
          <h2 class="dash-title">{{ group.name }}</h2>
          <p class="dash-note">
            {{ plural(group.jobs.length, "job") }} ·
            {{ plural(group.totals.week, "man-day") }} ·
            {{ plural(group.totals.loads, "load") }} ·
            {{ formatTons(group.totals.tons) }} tons
          </p>
        </div>
      </div>
      <v-data-table
        class="dash-table ws-table"
        :headers="headers"
        :items="group.jobs"
        :loading="loadingData"
        item-key="id"
        sort-by="week"
        sort-desc
        :items-per-page="100"
        hide-default-footer
        mobile-breakpoint="760"
      >
        <template v-slot:item.jobId="{ item }">
          <a :href="`/jobs/cost/${item.id}`" target="_blank" class="dash-job">{{
            item.jobId
          }}</a>
          <div class="dash-sub">{{ item.address }}</div>
        </template>
        <template v-for="slot in daySlots" v-slot:[slot]="{ value }">
          <span :key="slot" :class="value ? 'dash-num' : 'dash-sub'">{{
            value ? numberWithCommas(value) : "-"
          }}</span>
        </template>
        <template v-slot:item.week="{ item }">
          <span class="dash-figure ws-figure">{{
            numberWithCommas(item.week)
          }}</span>
        </template>
        <template v-slot:item.toDate="{ item }">
          <div class="dash-mandays">
            <man-day-gauge
              v-if="item.budget > 0"
              :percent="item.percent"
              class="mb-1"
            />
            <span class="dash-num">{{ numberWithCommas(item.toDate) }}</span>
            <span class="dash-sub" v-if="item.budget > 0">
              of {{ numberWithCommas(item.budget) }}</span
            >
            <span class="dash-sub" v-else> no budget set</span>
          </div>
        </template>
        <template v-slot:item.loads="{ item }">
          <span :class="item.loads ? 'dash-num' : 'dash-sub'">{{
            item.loads || "-"
          }}</span>
        </template>
        <template v-slot:item.yards="{ item }">
          <span :class="item.yards ? 'dash-num' : 'dash-sub'">{{
            item.yards ? numberWithCommas(item.yards) : "-"
          }}</span>
          <div class="dash-sub" v-if="item.minis">
            + {{ plural(item.minis, "mini") }}
          </div>
        </template>
        <template v-slot:item.tons="{ item }">
          <span :class="item.tons ? 'dash-num' : 'dash-sub'">{{
            item.tons ? formatTons(item.tons) : "-"
          }}</span>
          <div class="dash-sub" v-if="item.pendingTickets">
            {{ item.pendingTickets }} no ticket yet
          </div>
        </template>
        <template v-slot:body.append="{ isMobile }">
          <tr v-if="!isMobile && group.jobs.length > 1" class="ws-total">
            <td>Total</td>
            <td v-for="(n, i) in group.totals.days" :key="i" class="text-right">
              {{ n ? numberWithCommas(n) : "-" }}
            </td>
            <td class="text-right">
              <span class="dash-figure ws-figure">{{
                numberWithCommas(group.totals.week)
              }}</span>
            </td>
            <td></td>
            <td class="text-right">{{ group.totals.loads || "-" }}</td>
            <td class="text-right">
              {{
                group.totals.yards ? numberWithCommas(group.totals.yards) : "-"
              }}
            </td>
            <td class="text-right">
              {{ group.totals.tons ? formatTons(group.totals.tons) : "-" }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import fb from "@/main";
import { orderBy, sum, uniq, chunk } from "lodash";
import ManDayGauge from "../components/dashboard/ManDayGauge";
import moment from "moment";

const DAY = "YYYY-MM-DD";
const TEST_JOB_IDS = ["12-3456"];
const NO_OM = "none";

// Debris hauled off a job by one closed truck request. A load is any haul;
// dropping off an empty roll-off container or moving minis isn't one.
// `shares` counts the requests carrying each dump ticket number: a packer
// that picks up from several jobs dumps once, and every one of those
// requests carries the same ticket, so its weight is split between them.
const garbageFor = (tr, shares) => {
  const hauled =
    tr.requestType !== "Mini Pickup/Dropoff" &&
    !(tr.requestType === "Roll Off" && tr.pickupType === "Drop Off Container");
  let yards = 0;
  let minis = 0;
  if (tr.requestType === "Roll Off") {
    if (hauled) yards = Number(tr.qty) || 0;
  } else if (tr.requestType === "Packer") {
    const breakdown = tr.driverQuantityBreakdown;
    if (breakdown) {
      yards =
        (breakdown.looseYardsRegular || 0) + (breakdown.looseYardsHeavy || 0);
      minis = (breakdown.minisRegular || 0) + (breakdown.minisHeavy || 0);
    } else {
      const qty = Number(tr.closingQty || tr.qty) || 0;
      if (tr.qtyType === "Yards") yards = qty;
      if (tr.qtyType === "Mini Containers") minis = qty;
    }
  }
  const tickets = Object.values(tr.dumpTickets || {}).filter(Boolean);
  const tons = sum(
    tickets.map(
      t =>
        loadWeightToTons(t.loadWeight, t.loadWeightType) /
        (shares[t.ticketNumber] || 1)
    )
  );
  return {
    loads: hauled ? 1 : 0,
    yards,
    minis,
    tons,
    pendingTickets: hauled && !tickets.length ? 1 : 0
  };
};

// Same rule as TruckRequestsV2 and createCSV: Cubic Yards is a volume, so it
// is left out, and a weight over 100 is pounds, anything else tons.
const loadWeightToTons = (loadWeight, loadWeightType) => {
  if (loadWeight === null || loadWeight === undefined || loadWeight === "")
    return 0;
  if (loadWeightType === "Cubic Yards") return 0;
  const num = Number(loadWeight);
  if (isNaN(num)) return 0;
  return num > 100 ? num / 2000 : num;
};

const emptyTotals = () => ({
  days: [0, 0, 0, 0, 0],
  week: 0,
  loads: 0,
  yards: 0,
  minis: 0,
  tons: 0,
  pendingTickets: 0
});

const addTo = (totals, job) => {
  job.days.forEach((n, i) => (totals.days[i] += n));
  ["week", "loads", "yards", "minis", "tons", "pendingTickets"].forEach(
    k => (totals[k] += job[k])
  );
  return totals;
};

export default {
  name: "WeeklySummary",
  components: {
    ManDayGauge
  },
  data: () => ({
    loading: true,
    loadingData: false,
    error: "",
    weekStart: moment().startOf("isoWeek"),
    datePickerDialog: false,
    pickedDate: moment().format(DAY),
    jobs: []
  }),
  computed: {
    todayKey() {
      return moment().format(DAY);
    },
    dayKeys() {
      return [0, 1, 2, 3, 4].map(i =>
        this.weekStart
          .clone()
          .add(i, "days")
          .format(DAY)
      );
    },
    daySlots() {
      return this.dayKeys.map((d, i) => `item.d${i}`);
    },
    isThisWeek() {
      return this.weekStart.isSame(moment().startOf("isoWeek"), "day");
    },
    // Friday not over yet, so today's numbers can still grow.
    inProgress() {
      return this.dayKeys[4] >= this.todayKey;
    },
    throughLabel() {
      return moment(this.todayKey).format("dddd");
    },
    weekLabel() {
      const mon = this.weekStart;
      const fri = mon.clone().add(4, "days");
      const sameYear = fri.isSame(moment(), "year");
      const end = mon.isSame(fri, "month")
        ? fri.format("D")
        : fri.format("MMM D");
      return `Week of ${mon.format("MMM D")} to ${end}${
        sameYear ? "" : fri.format(", YYYY")
      }`;
    },
    headers() {
      return [
        { text: "Job", value: "jobId", width: 260 },
        ...this.dayKeys.map((d, i) => ({
          text: moment(d).format("ddd D"),
          value: `d${i}`,
          align: "right",
          width: 64
        })),
        { text: "Week", value: "week", align: "right", width: 80 },
        { text: "Man days to date", value: "toDate", width: 190 },
        { text: "Loads", value: "loads", align: "right", width: 70 },
        { text: "Yards", value: "yards", align: "right", width: 90 },
        { text: "Tons", value: "tons", align: "right", width: 100 }
      ];
    },
    // A job with several OMs goes under each of them. No OM comes last.
    groups() {
      const groups = {};
      this.jobs.forEach(job => {
        job.oms.forEach(om => {
          if (!groups[om.key]) {
            groups[om.key] = { key: om.key, name: om.name, jobs: [] };
          }
          groups[om.key].jobs.push(job);
        });
      });
      return orderBy(
        Object.values(groups).map(group => ({
          ...group,
          totals: group.jobs.reduce(addTo, emptyTotals())
        })),
        [g => g.key !== NO_OM, g => g.totals.week],
        ["desc", "desc"]
      );
    },
    // every job once, however many OMs it has
    totals() {
      return this.jobs.reduce(addTo, emptyTotals());
    },
    headline() {
      if (!this.jobs.length) return "Nothing on the books this week";
      return `${this.plural(this.totals.week, "man-day")} on ${this.plural(
        this.jobs.length,
        "job"
      )}`;
    },
    garbageLine() {
      const { loads, yards, minis, tons } = this.totals;
      if (!loads) return "No loads hauled.";
      const parts = [`${this.numberWithCommas(yards)} yards`];
      if (minis) parts.push(this.plural(minis, "mini"));
      parts.push(`${this.formatTons(tons)} tons`);
      return `${this.plural(loads, "load")} hauled: ${parts.join(", ")}.`;
    }
  },
  created() {
    this.jobSiteCache = {};
    const week = this.$route.query.week;
    if (week && moment(week, DAY, true).isValid()) {
      this.weekStart = moment.min(moment(week), moment()).startOf("isoWeek");
    }
    this.pickedDate = this.weekStart.format(DAY);
    this.getWeek();
  },
  methods: {
    goToWeek(step) {
      this.goToDate(
        this.weekStart
          .clone()
          .add(step, "weeks")
          .format(DAY)
      );
    },
    goToDate(date) {
      this.datePickerDialog = false;
      this.weekStart = moment(date).startOf("isoWeek");
      this.pickedDate = this.weekStart.format(DAY);
      this.$router
        .replace({ query: { week: this.weekStart.format(DAY) } })
        .catch(() => {});
      this.getWeek();
    },
    async getWeek() {
      const weekStart = this.weekStart;
      const [mon, , , , fri] = this.dayKeys;
      const today = moment().format(DAY);
      this.loadingData = true;
      this.error = "";
      try {
        // Any job that punched in this week has a lastPunchDate on or after
        // Monday, so this is every candidate and then some.
        const [statsRes, truckRes] = await Promise.all([
          fb.db
            .collection("jobStats")
            .where("lastPunchDate", ">=", mon)
            .get(),
          fb.db
            .collection("truckRequests")
            .where("requestDate", ">=", mon)
            .where("requestDate", "<=", fri)
            .get()
        ]);
        if (weekStart !== this.weekStart) return;
        const stats = {};
        statsRes.forEach(doc => (stats[doc.id] = doc.data()));

        // manDayStatsJob runs at 6am and counts that morning's punch-ins as
        // well, so the day it last ran, and anything after, is counted live.
        const refreshedAt = Math.max(
          0,
          ...Object.values(stats).map(s =>
            s.updatedAt && s.updatedAt.toDate
              ? s.updatedAt.toDate().getTime()
              : 0
          )
        );
        const liveFrom = refreshedAt ? moment(refreshedAt).format(DAY) : today;
        const liveDays = this.dayKeys.filter(d => d >= liveFrom && d <= today);
        const live = await this.getLiveManDays(liveDays);
        if (weekStart !== this.weekStart) return;

        const closed = [];
        truckRes.forEach(doc => {
          if (doc.get("status") === "closed") closed.push(doc.data());
        });
        const shares = {};
        closed.forEach(tr =>
          Object.values(tr.dumpTickets || {}).forEach(t => {
            if (t && t.ticketNumber) {
              shares[t.ticketNumber] = (shares[t.ticketNumber] || 0) + 1;
            }
          })
        );
        const garbage = {};
        closed.forEach(tr => {
          if (!tr.jobSite || !tr.jobSite.id) return;
          const g = garbageFor(tr, shares);
          const totals = garbage[tr.jobSite.id] || {
            loads: 0,
            yards: 0,
            minis: 0,
            tons: 0,
            pendingTickets: 0
          };
          Object.keys(totals).forEach(k => (totals[k] += g[k]));
          garbage[tr.jobSite.id] = totals;
        });

        const manDaysOn = (jobSiteId, day) => {
          if (liveDays.includes(day)) {
            return (live[jobSiteId] && live[jobSiteId][day]) || 0;
          }
          const days = (stats[jobSiteId] && stats[jobSiteId].days) || {};
          return (days[day] && days[day].manDays) || 0;
        };
        const jobSiteIds = uniq([
          ...Object.keys(stats),
          ...Object.keys(live),
          ...Object.keys(garbage).filter(
            id => garbage[id].loads || garbage[id].tons
          )
        ]);
        await this.fetchJobSites(jobSiteIds);
        if (weekStart !== this.weekStart) return;

        const jobs = [];
        jobSiteIds.forEach(id => {
          const jobSite = this.jobSiteCache[id];
          if (!jobSite || !this.isJob(jobSite)) return;
          const days = this.dayKeys.map(d => manDaysOn(id, d));
          const g = garbage[id] || {};
          if (!sum(days) && !g.loads && !g.tons) return;
          // Man days through Friday: jobStats before the live days, then the
          // live counts.
          const statsDays = (stats[id] && stats[id].days) || {};
          const toDate =
            sum(
              Object.keys(statsDays)
                .filter(d => d < liveFrom && d <= fri)
                .map(d => statsDays[d].manDays)
            ) + sum(liveDays.map(d => (live[id] && live[id][d]) || 0));
          const budget = jobSite.manDays || 0;
          const oms = (jobSite.projectManagers || []).map(pm => ({
            key: pm.id || pm.email || pm.name,
            name: this.fixName(pm.name)
          }));
          jobs.push({
            id,
            jobId: jobSite.jobId,
            address: [jobSite.street, jobSite.city].filter(Boolean).join(", "),
            oms: oms.length ? oms : [{ key: NO_OM, name: "No OM" }],
            days,
            d0: days[0],
            d1: days[1],
            d2: days[2],
            d3: days[3],
            d4: days[4],
            week: sum(days),
            toDate,
            budget,
            percent: budget > 0 ? Math.round((toDate / budget) * 100) : 0,
            loads: g.loads || 0,
            yards: g.yards || 0,
            minis: g.minis || 0,
            tons: g.tons || 0,
            pendingTickets: g.pendingTickets || 0
          });
        });
        this.jobs = jobs;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("WeeklySummary getWeek error", err);
        this.error = `Couldn't load this week: ${err.message || err}`;
      }
      if (weekStart !== this.weekStart) return;
      this.loading = false;
      this.loadingData = false;
    },
    // Distinct employees punched in per job per day, the count jobStats
    // keeps, for days it hasn't counted in full yet.
    async getLiveManDays(liveDays) {
      if (!liveDays.length) return {};
      const res = await fb.db
        .collection("punchRecords")
        .where(
          "time",
          ">",
          moment(liveDays[0])
            .startOf("day")
            .toDate()
        )
        .where(
          "time",
          "<",
          moment(liveDays[liveDays.length - 1])
            .endOf("day")
            .toDate()
        )
        .where("direction", "==", "in")
        .get();
      const employees = {};
      res.forEach(doc => {
        const p = doc.data();
        if (!p.jobSite || !p.employee || !p.time || !p.time.toDate) return;
        const day = moment(p.time.toDate()).format(DAY);
        const key = `${p.jobSite}|${day}`;
        if (!employees[key]) employees[key] = new Set();
        employees[key].add(p.employee);
      });
      const live = {};
      Object.keys(employees).forEach(key => {
        const [jobSiteId, day] = key.split("|");
        if (!live[jobSiteId]) live[jobSiteId] = {};
        live[jobSiteId][day] = employees[key].size;
      });
      return live;
    },
    // Job sites by id, 10 per query (the SDK's limit for "in"), kept for the
    // life of the page so moving between weeks only fetches new ones.
    async fetchJobSites(ids) {
      const missing = ids.filter(id => id && !(id in this.jobSiteCache));
      const results = await Promise.all(
        chunk(missing, 10).map(ids =>
          fb.db
            .collection("jobSites")
            .where(fb.firebase.firestore.FieldPath.documentId(), "in", ids)
            .get()
        )
      );
      missing.forEach(id => (this.jobSiteCache[id] = null));
      results.forEach(res =>
        res.forEach(
          doc => (this.jobSiteCache[doc.id] = { id: doc.id, ...doc.data() })
        )
      );
    },
    // Leaves out the test job, internal 15-xxxx jobs and external truck
    // request sites, which are carting-client locations rather than jobs.
    isJob(jobSite) {
      const jobId = jobSite.jobId || "";
      return (
        !TEST_JOB_IDS.includes(jobId) &&
        !jobId.startsWith("15") &&
        !jobId.startsWith("TR") &&
        !jobSite.external
      );
    },
    formatTons(tons) {
      return this.numberWithCommas(Math.round(tons * 10) / 10);
    },
    plural(n, word) {
      return `${this.numberWithCommas(n)} ${word}${n === 1 ? "" : "s"}`;
    },
    numberWithCommas(x) {
      if (!x) return 0;
      const [whole, fraction] = x.toString().split(".");
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return fraction ? `${withCommas}.${fraction}` : withCommas;
    },
    // Same tidy-up as the Office Dashboard: names on jobSites keep whatever
    // casing was typed at signup.
    fixName(n) {
      return (n || "")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .map(w => {
          if (w !== w.toLowerCase() && w !== w.toUpperCase()) return w;
          return w.replace(
            /[^'’-]+/g,
            p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
          );
        })
        .join(" ");
    },
    downloadCSV() {
      const headers = [
        "OM",
        "Job ID",
        "Address",
        ...this.dayKeys.map(d => moment(d).format("ddd M/D")),
        "Week Man Days",
        "Man Days To Date",
        "Man Days Budget",
        "Loads",
        "Yards",
        "Minis",
        "Tons",
        "Loads Without Ticket"
      ];
      const rows = [];
      this.groups.forEach(group =>
        orderBy(group.jobs, "week", "desc").forEach(job =>
          rows.push([
            group.name,
            job.jobId,
            job.address,
            ...job.days,
            job.week,
            job.toDate,
            job.budget || "",
            job.loads,
            job.yards,
            job.minis,
            Math.round(job.tons * 10) / 10,
            job.pendingTickets
          ])
        )
      );
      const csvContent = [headers, ...rows]
        .map(row =>
          row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute(
        "download",
        `weekly-summary-${this.weekStart.format(DAY)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
/* Same look as the Office Dashboard (OfficeDashboard.vue). */
.dash {
  --ink: #1c2534;
  --muted: #5d6878;
  --line: #dde3ea;
  --cobalt: #15489e;
  --hivis: #e8650f;
  --hivis-text: #a84a08;
  --over: #c62828;
  --ok: #2e7d32;
  --display: "Barlow Semi Condensed", "Barlow", Roboto, sans-serif;
  font-family: "Barlow", Roboto, sans-serif;
  color: var(--ink);
  max-width: 1480px;
}

/* the week */
.dash-hero {
  padding: 8px 4px 20px;
}
.dash-dateline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-left: -6px;
}
.dash-date {
  font-family: var(--display);
  font-size: 17px;
  font-weight: 600;
  color: var(--cobalt);
  padding: 4px 8px;
  border-radius: 6px;
}
.dash-date:hover {
  background: rgba(21, 72, 158, 0.08);
}
.dash-date:focus-visible {
  outline: 2px solid var(--cobalt);
  outline-offset: 2px;
}
.dash-headline {
  font-family: var(--display);
  font-size: 44px;
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 6px 0 4px;
  font-variant-numeric: tabular-nums;
}
.dash-subline {
  font-size: 16px;
  color: var(--muted);
  margin: 0;
  max-width: 70ch;
}
.dash-subline--note {
  font-size: 14px;
  margin-top: 6px;
}
.dash ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}
.dash-quiet-btn {
  color: var(--muted) !important;
}

/* panels */
.dash-panel {
  border-color: var(--line) !important;
  border-radius: 10px !important;
  margin-bottom: 24px;
  overflow: hidden;
}
.dash-panel__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 18px 20px 12px;
}
.dash-title {
  font-family: var(--display);
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}
.dash-note {
  font-size: 14px;
  color: var(--muted);
  margin: 2px 0 0;
  font-variant-numeric: tabular-nums;
}

/* tables */
.dash-table ::v-deep th {
  font-size: 13px !important;
  font-weight: 500;
  color: var(--muted) !important;
  white-space: nowrap;
  border-bottom-color: var(--line) !important;
}
.dash-table ::v-deep th:first-child,
.dash-table ::v-deep td:first-child {
  padding-left: 20px !important;
}
.dash-table ::v-deep td {
  font-size: 14px !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  height: auto !important;
  vertical-align: top;
  border-bottom-color: var(--line) !important;
  font-variant-numeric: tabular-nums;
}
.dash-figure {
  font-family: var(--display);
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.ws-figure {
  font-size: 20px;
}
.dash-job {
  font-family: var(--display);
  font-size: 16px;
  font-weight: 600;
  color: var(--cobalt);
  text-decoration: none;
}
.dash-job:hover {
  text-decoration: underline;
}
.dash-sub {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.4;
}
.dash-num {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.dash-mandays {
  min-width: 130px;
}
.ws-table ::v-deep .ws-total td {
  font-weight: 600;
  background: #f6f8fb;
  border-top: 1px solid var(--line);
}
.ws-table ::v-deep .ws-total:hover td {
  background: #f6f8fb !important;
}
.dash-empty {
  color: var(--muted);
  font-size: 15px;
  margin: 24px 0;
}

@media (max-width: 600px) {
  .dash-headline {
    font-size: 32px;
  }
}

@media print {
  .dash-noprint {
    display: none !important;
  }
  .dash-print {
    border: 0 !important;
  }
}
</style>
